import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Unit } from "@/lib/types/unit";
import { Card, CardContent } from "@/components/ui/card";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { UnitInfoDialog } from "./UnitInfoDialog";
import { useComparisonModals } from "./ComparisonModalManager";
import { Info } from "lucide-react";

interface TransportSelectionProps {
  open: boolean;
  onClose: () => void;
  transports: Unit[];
  onSelect: (transport: Unit | null) => void;
  allowNone?: boolean;
}

export function TransportSelection({
  open,
  onClose,
  transports,
  onSelect,
  allowNone = false,
}: TransportSelectionProps) {
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedTransportForInfo, setSelectedTransportForInfo] = useState<Unit | null>(null);
  const { openComparisonModal } = useComparisonModals();

  const handleSelect = (transport: Unit | null) => {
    onSelect(transport);
    onClose();
  };

  const handleImageError = (descriptorName: string) => {
    setImageErrors((prev) => new Set(prev).add(descriptorName));
  };

  const handleInfoClick = (e: React.MouseEvent, transport: Unit) => {
    e.stopPropagation(); // Prevent triggering transport selection
    setSelectedTransportForInfo(transport);
    setInfoDialogOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Transport</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {allowNone && (
            <Card className="cursor-pointer hover:bg-accent" onClick={() => handleSelect(null)}>
              <CardContent className="p-4">
                <div className="font-medium">No Transport</div>
              </CardContent>
            </Card>
          )}
          {transports.map((transport) => {
            const hasImageError = imageErrors.has(transport.descriptorName);
            return (
              <Card
                key={transport.descriptorName}
                className="cursor-pointer hover:bg-accent overflow-hidden relative"
                onClick={() => handleSelect(transport)}
              >
                <CardContent className="p-4 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="font-medium">{transport.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {transport.commandPoints} CP
                      </div>
                    </div>
                  </div>
                </CardContent>
                {/* Overlay Info Button (top-right) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // On desktop, open comparison modal directly; on mobile, use regular modal
                    if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                      openComparisonModal(transport);
                    } else {
                      handleInfoClick(e, transport);
                    }
                  }}
                  className="absolute top-[2px] right-[2px] w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center shadow-lg transition-colors z-20"
                  aria-label="Transport information"
                >
                  <Info className="h-3.5 w-3.5 text-white" />
                </button>
                {!hasImageError && (
                  <>
                    <img
                      src={getUnitImagePath(transport.descriptorName)}
                      alt=""
                      className="absolute inset-y-0 right-0 w-2/3 h-full object-cover opacity-100 pointer-events-none"
                      onError={() => handleImageError(transport.descriptorName)}
                    />
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(to right, var(--card) 0%, var(--card) 40%, transparent 100%)',
                      }}
                    />
                  </>
                )}
              </Card>
            );
          })}
        </div>
      </DialogContent>
      {selectedTransportForInfo && (
        <UnitInfoDialog
          open={infoDialogOpen}
          onOpenChange={setInfoDialogOpen}
          unit={selectedTransportForInfo}
        />
      )}
    </Dialog>
  );
}

